/* eslint-disable no-dupe-keys */
/* eslint-disable react-hooks/exhaustive-deps */
// =============================================================================
// HarborDataTable
//
// Harbor-local copy of RouteDataTable. The only difference is in the
// headerCellContent style: width is "auto" (not "100%") and gap is 2 (not 12)
// so the sort icon sits flush next to the column label and does not overlap
// the adjacent filter icon.
//
// RouteDataTable is shared by other modules and must NOT be modified.
// =============================================================================
import React, { useState, useMemo, useEffect, useCallback, useRef } from "react";
import { createPortal } from 'react-dom';
import {
  View,
  Text,
  FlatList,
  ScrollView,
  StyleSheet,
  TouchableOpacity,
  TouchableWithoutFeedback,
  Dimensions,
  useWindowDimensions,
  Platform,
  Animated,
} from "react-native";
import MaterialIcons from "react-native-vector-icons/MaterialIcons";
import FontFamily from "../../constants/fonts";
import LHOverlay from "../../components/LHOverlay";
import LhButton from "../../components/LhButton";
import DateRangeFilterDropdown from "./components/DateRangeFilterDropdown";
import HarborFilterdropdown from "./components/HarborFilterdropdown";
import { useDarkStyles } from '../../theme/useDarkStyles';

// ---------------------------------------------------------------------------
// Skeleton loader
// ---------------------------------------------------------------------------
interface SkeletonRowsProps {
  columns: Column[];
  columnWidths: { [key: string]: { minWidth: number; flex: number } };
  rowCount?: number;
}

const HarborSkeletonRows: React.FC<SkeletonRowsProps> = React.memo(
  ({ columns, columnWidths, rowCount = 10 }) => {
  const skeletonStyles = useDarkStyles(baseSkeletonStyles);
    const pulse = React.useRef(new Animated.Value(0.35)).current;

    React.useEffect(() => {
      const loop = Animated.loop(
        Animated.sequence([
          Animated.timing(pulse, {
            toValue: 0.85,
            duration: 750,
            useNativeDriver: Platform.OS !== 'web',
          }),
          Animated.timing(pulse, {
            toValue: 0.35,
            duration: 750,
            useNativeDriver: Platform.OS !== 'web',
          }),
        ])
      );
      loop.start();
      return () => loop.stop();
    }, [pulse]);

    return (
      <>
        {Array.from({ length: rowCount }).map((_, rowIdx) => (
          <View key={rowIdx} style={skeletonStyles.row}>
            {columns.map((col, colIdx) => {
              const cw = columnWidths[col.columnKey];
              const minWidth = cw ? cw.minWidth : (col.width ?? 120);
              const flex = cw ? cw.flex : 0;
              const barPct = 45 + ((rowIdx * 11 + colIdx * 17) % 40);
              return (
                <View key={col.columnKey} style={[skeletonStyles.cell, { minWidth, flex }]}>
                  <Animated.View
                    style={[skeletonStyles.bar, { width: `${barPct}%`, opacity: pulse }]}
                  />
                </View>
              );
            })}
          </View>
        ))}
      </>
    );
  }
);

const baseSkeletonStyles = StyleSheet.create({
  row: {
    flexDirection: 'row',
    height: 48,
    minHeight: 48,
    borderBottomWidth: 2,
    borderBottomColor: '#D7E5F8',
    width: '100%',
    alignItems: 'center',
  },
  cell: {
    height: 48,
    paddingHorizontal: 16,
    paddingVertical: 14,
    justifyContent: 'center',
    overflow: 'hidden',
  },
  bar: {
    height: 12,
    borderRadius: 6,
    backgroundColor: '#C8D6EA',
  },
});

type ColumnType = "toggle" | "checkbox";

export interface DateRangeFilter {
  fromDate: Date | null;
  toDate: Date | null;
  onFromChange: (date: Date | null) => void;
  onToChange: (date: Date | null) => void;
  onClear: () => void;
}

type Column = {
  columnKey: string;
  header: string;
  type?: ColumnType;
  renderCell?: (item: any) => React.ReactNode;
  formatter?: (value: any) => string;
  flexGrow?: number;
  width?: number;
  getSortValue?: (item: any) => any;
  filterOptions?: { label: string; value: string }[];
  dateRangeFilter?: DateRangeFilter;
  sortable?: boolean;
};

type Props = {
  data: any[];
  columns: Column[];
  tableHeader: string;
  tableDescription: string;
  maxRecordsToShow?: number;
  onRowClick?: (rowData: any) => void;
  onPageSizeChange?: (size: number) => void;
  onSort?: (sortKey: string, direction: 'asc' | 'desc') => void;
  externalPage?: number;
  externalTotalPages?: number;
  onExternalPageChange?: (page: number) => void;
  columnFlexValues?: { [columnKey: string]: number };
  showHeaderTopBorder?: boolean;
  expansionRenderer?: (item: any) => React.ReactNode;
  isLoading?: boolean;
  isFirstPage?: boolean;
  isLastPage?: boolean;
  columnFilters?: Record<string, string[]>;
  onColumnFilter?: (columnKey: string, values: string[]) => void;
  locale?: {
    rowsPerPageLabel?: string;
    pageLabel?: string;
    pageOfLabel?: string;
    firstPageLabel?: string;
    prevPageLabel?: string;
    nextPageLabel?: string;
    lastPageLabel?: string;
  };
};

// ── Header row ──────────────────────────────────────────────────────────────
interface TableHeaderRowProps {
  columns: Column[];
  sortConfig: { key: string; direction: 'asc' | 'desc' } | null;
  columnWidths: { [key: string]: { minWidth: number; flex: number } };
  showHeaderTopBorder: boolean;
  isIOSMobile: boolean;
  onSort: (columnKey: string) => void;
  columnFilters: Record<string, string[]>;
  dateRangeActive: Record<string, boolean>;
  activeFilterKey: string | null;
  onOpenFilter: (columnKey: string, options: { label: string; value: string }[], btnRef: any) => void;
  onOpenDateRangeFilter: (columnKey: string, dateRangeFilter: DateRangeFilter, btnRef: any) => void;
}

const HeaderTooltip: React.FC<{
  visible: boolean;
  position: { x: number; y: number } | null;
  text: string;
}> = ({ visible, position, text }) => {
  if (Platform.OS !== 'web' || !visible || !position || !text) return null;
  const win: any = typeof globalThis !== 'undefined' ? (globalThis as any) : {};
  if (!win.document) return null;

  const overlay = (
    <View
      style={{
        position: 'fixed' as any,
        left: position.x + 12,
        top: position.y - 8,
        maxWidth: 260,
        zIndex: 2147483647,
        pointerEvents: 'none' as any,
      }}
    >
      <View
        style={{
          paddingHorizontal: 10,
          paddingVertical: 6,
          borderRadius: 6,
          backgroundColor: '#1C1C1C',
        }}
      >
        <Text style={{ color: '#FFFFFF', fontSize: 12, fontWeight: '400', lineHeight: 16 }}>
          {text}
        </Text>
      </View>
    </View>
  );

  return createPortal(overlay, win.document.body);
};

const HarborTableHeaderRow = React.memo(
  ({
    columns,
    sortConfig,
    columnWidths,
    showHeaderTopBorder,
    isIOSMobile,
    onSort,
    columnFilters,
    dateRangeActive,
    activeFilterKey,
    onOpenFilter,
    onOpenDateRangeFilter,
  }: TableHeaderRowProps) => {
  const styles = useDarkStyles(baseStyles);
    const filterBtnRefs = React.useRef<Record<string, any>>({});
    const [hoveredHeader, setHoveredHeader] = useState<{ text: string; x: number; y: number } | null>(null);
    return (
      <>
        <HeaderTooltip
          visible={hoveredHeader !== null}
          position={hoveredHeader}
          text={hoveredHeader?.text ?? ''}
        />
        <View
          style={[
            styles.headerRow,
            showHeaderTopBorder
              ? { borderTopWidth: 2, borderTopColor: '#D7E5F8' }
              : { borderTopWidth: 0, borderTopColor: 'transparent' },
          ]}
        >
          {columns.map((col) => {
          const isSortable = col.sortable !== false && col.columnKey !== '_actions';
          const isSorted = sortConfig?.key === col.columnKey;
          const hasFilter = !!col.filterOptions?.length;
          const hasDRFilter = !!col.dateRangeFilter;
          const isFiltered = (columnFilters[col.columnKey]?.length ?? 0) > 0;
          const isDRFiltered = dateRangeActive[col.columnKey] ?? false;
          const isFilterOpen = activeFilterKey === col.columnKey;
          const webHeaderHandlers: any = Platform.OS === 'web'
            ? {
                onMouseEnter: (e: any) => {
                  const x = e?.clientX ?? e?.nativeEvent?.clientX ?? 0;
                  const y = e?.clientY ?? e?.nativeEvent?.clientY ?? 0;
                  setHoveredHeader({ text: col.header, x, y });
                },
                onMouseMove: (e: any) => {
                  const x = e?.clientX ?? e?.nativeEvent?.clientX ?? 0;
                  const y = e?.clientY ?? e?.nativeEvent?.clientY ?? 0;
                  setHoveredHeader((prev) => (prev ? { ...prev, x, y } : null));
                },
                onMouseLeave: () => setHoveredHeader(null),
              }
            : {};
          return (
            <View
              key={col.columnKey}
              style={[
                styles.cellContainer,
                {
                  justifyContent: isIOSMobile ? 'center' : 'flex-start',
                  flexDirection: 'row',
                  alignItems: 'center',
                },
                columnWidths[col.columnKey] && {
                  minWidth: columnWidths[col.columnKey].minWidth,
                  flex: columnWidths[col.columnKey].flex,
                  ...(Platform.OS !== 'web' && {
                    width: columnWidths[col.columnKey].minWidth,
                  }),
                },
              ]}
            >
              {/* Label – tappable for sort, takes remaining space, text truncates */}
              <TouchableOpacity
                onPress={() => isSortable && onSort(col.columnKey)}
                style={{ flex: 1, minWidth: 0, justifyContent: 'center' }}
                activeOpacity={isSortable ? 0.7 : 1}
                {...webHeaderHandlers}
              >
                <Text style={styles.headerCellText} numberOfLines={1}>{col.header}</Text>
              </TouchableOpacity>
              {/* Sort icon – fixed width, sits between label and filter */}
              {isSortable && (
                <TouchableOpacity
                  onPress={() => onSort(col.columnKey)}
                  style={styles.sortIconBtn}
                  activeOpacity={0.7}
                  hitSlop={{ top: 6, bottom: 6, left: 2, right: 2 }}
                >
                  {!isSorted ? (
                    <MaterialIcons name="unfold-more" size={16} color="#616161" />
                  ) : sortConfig?.direction === 'asc' ? (
                    <MaterialIcons name="keyboard-arrow-up" size={16} color="#616161" />
                  ) : (
                    <MaterialIcons name="keyboard-arrow-down" size={16} color="#616161" />
                  )}
                </TouchableOpacity>
              )}
              {/* Filter icon – for columns with filterOptions (checkbox list) */}
              {hasFilter && (
                <TouchableOpacity
                  ref={(r) => { filterBtnRefs.current[col.columnKey] = r; }}
                  onPress={() =>
                    onOpenFilter(col.columnKey, col.filterOptions!, filterBtnRefs.current[col.columnKey])
                  }
                  style={[styles.filterIconBtn, isFilterOpen && styles.filterIconBtnActive]}
                  activeOpacity={0.6}
                  hitSlop={{ top: 6, bottom: 6, left: 2, right: 2 }}
                >
                  <MaterialIcons
                    name="filter-alt"
                    size={15}
                    color={isFiltered || isFilterOpen ? '#005CBC' : '#9E9E9E'}
                  />
                </TouchableOpacity>
              )}
              {/* Filter icon – for columns with dateRangeFilter */}
              {hasDRFilter && (
                <TouchableOpacity
                  ref={(r) => { filterBtnRefs.current[col.columnKey] = r; }}
                  onPress={() =>
                    onOpenDateRangeFilter(col.columnKey, col.dateRangeFilter!, filterBtnRefs.current[col.columnKey])
                  }
                  style={[styles.filterIconBtn, isFilterOpen && styles.filterIconBtnActive]}
                  activeOpacity={0.6}
                  hitSlop={{ top: 6, bottom: 6, left: 2, right: 2 }}
                >
                  <MaterialIcons
                    name="filter-alt"
                    size={15}
                    color={isDRFiltered || isFilterOpen ? '#005CBC' : '#9E9E9E'}
                  />
                </TouchableOpacity>
              )}
            </View>
            );
          })}
        </View>
      </>
    );
  }
);

const HarborDataTable = ({
  data,
  columns,
  maxRecordsToShow = 10,
  onRowClick,
  onPageSizeChange,
  onSort,
  externalPage,
  externalTotalPages,
  onExternalPageChange,
  columnFlexValues,
  showHeaderTopBorder = true,
  expansionRenderer,
  isLoading = false,
  isFirstPage,
  isLastPage,
  columnFilters = {},
  onColumnFilter,
  locale,
}: Props) => {
  const styles = useDarkStyles(baseStyles);
  const isExternalPagination = externalPage !== undefined && externalTotalPages !== undefined;
  const [selectedRowIndex, setSelectedRowIndex] = useState<number | null>(null);
  const [sortConfig, setSortConfig] = useState<{
    key: string;
    direction: "asc" | "desc";
  } | null>(null);
  const [overlayVisible, setOverlayVisible] = useState(false);
  const [overlayContent, setOverlayContent] = useState<any>(null);
  const [currentPage, setCurrentPage] = useState(1);
  const [rowsPerPage, setRowsPerPage] = useState(maxRecordsToShow);
  const [dropdownVisible, setDropdownVisible] = useState(false);
  const [columnWidths, setColumnWidths] = useState<{
    [key: string]: { minWidth: number; flex: number };
  }>({});
  const [layoutCalculated, setLayoutCalculated] = useState(false);
  const [containerWidth, setContainerWidth] = useState(0);

  // ── Column filter dropdown ────────────────────────────────────────────────
  const [filterModal, setFilterModal] = useState<{
    columnKey: string;
    options: { label: string; value: string }[];
    top: number;
    left: number;
    width: number;
  } | null>(null);
  const filterModalRef = useRef(filterModal);
  useEffect(() => { filterModalRef.current = filterModal; }, [filterModal]);

  // ── Date range filter dropdown ────────────────────────────────────────────
  const [drFilterModal, setDrFilterModal] = useState<{
    columnKey: string;
    dateRangeFilter: DateRangeFilter;
    top: number;
    left: number;
  } | null>(null);
  const drFilterModalRef = useRef(drFilterModal);
  useEffect(() => { drFilterModalRef.current = drFilterModal; }, [drFilterModal]);

  const handleOpenFilter = useCallback((
    columnKey: string,
    options: { label: string; value: string }[],
    btnRef: any,
  ) => {
    if (filterModalRef.current?.columnKey === columnKey) {
      setFilterModal(null);
      return;
    }
    setDrFilterModal(null);
    if (btnRef) {
      btnRef.measure((_x: number, _y: number, w: number, h: number, pageX: number, pageY: number) => {
        const screenWidth = Dimensions.get('window').width;
        // Must match the rendered menu width below so the edge check is accurate
        const dropdownWidth = 280;
        const edgeMargin = 8;
        const rawLeft = pageX;
        // Right-align against the trigger when the menu would overflow the viewport,
        // then clamp so it can never extend past either screen edge.
        const preferredLeft =
          rawLeft + dropdownWidth + edgeMargin > screenWidth
            ? pageX + w - dropdownWidth
            : rawLeft;
        const maxLeft = screenWidth - dropdownWidth - edgeMargin;
        const left = Math.max(edgeMargin, Math.min(preferredLeft, maxLeft));
        setFilterModal({ columnKey, options, top: pageY + h + 4, left, width: dropdownWidth });
      });
    }
  }, []);

  const handleOpenDateRangeFilter = useCallback((
    columnKey: string,
    dateRangeFilter: DateRangeFilter,
    btnRef: any,
  ) => {
    if (drFilterModalRef.current?.columnKey === columnKey) {
      setDrFilterModal(null);
      return;
    }
    setFilterModal(null);
    if (btnRef) {
      btnRef.measure((_x: number, _y: number, w: number, h: number, pageX: number, pageY: number) => {
        const screenWidth = Dimensions.get('window').width;
        const dropdownWidth = 240;
        const edgeMargin = 8;
        const rawLeft = pageX;
        const left =
          rawLeft + dropdownWidth + edgeMargin > screenWidth
            ? Math.max(edgeMargin, pageX + w - dropdownWidth)
            : rawLeft;
        setDrFilterModal({ columnKey, dateRangeFilter, top: pageY + h + 4, left });
      });
    }
  }, []);

  // Compute which date-range columns currently have an active filter
  const dateRangeActive = useMemo(() => {
    const result: Record<string, boolean> = {};
    columns.forEach((col) => {
      if (col.dateRangeFilter) {
        result[col.columnKey] = !!(col.dateRangeFilter.fromDate || col.dateRangeFilter.toDate);
      }
    });
    return result;
  }, [columns]);

  const { width: screenWidth } = useWindowDimensions();
  const isIOSMobile = Platform.OS === "ios" && screenWidth < 768;

  const estimateTextWidth = (text: string, isBold: boolean = false) => {
    const baseWidth = Platform.OS === "web" ? 8 : 6;
    const boldMultiplier = isBold ? 1.2 : 1;
    let charCount = 0;
    const str = String(text);
    for (let i = 0; i < str.length; i++) {
      if (/[WM]/.test(str[i])) {
        charCount += 1.3;
      } else if (/[ilt1|]/.test(str[i])) {
        charCount += 0.6;
      } else {
        charCount += 1;
      }
    }
    return charCount * baseWidth * boldMultiplier;
  };

  const calculateColumnWidths = useMemo(() => {
    const widths: { [key: string]: { minWidth: number; flex: number } } = {};
    const padding = 24;
    const minColumnWidth = 120;
    const maxColumnWidth = 300;
    const safeData = Array.isArray(data) ? data : [];

    columns.forEach((col) => {
      let maxWidth = estimateTextWidth(col.header, true) + padding;

      safeData.forEach((item) => {
        let cellValue = item[col.columnKey];
        if (col.formatter && cellValue !== undefined && cellValue !== null) {
          cellValue = col.formatter(cellValue);
        }
        if (cellValue !== undefined && cellValue !== null) {
          const cellWidth = estimateTextWidth(String(cellValue)) + padding;
          maxWidth = Math.max(maxWidth, cellWidth);
        }
      });

      if (col.width !== undefined) {
        maxWidth = col.width;
      } else {
        maxWidth = Math.max(minColumnWidth, Math.min(maxWidth, maxColumnWidth));
      }

      let flex = 1;
      if (col.width !== undefined) {
        flex = 0;
      } else if (columnFlexValues?.[col.columnKey]) {
        flex = columnFlexValues[col.columnKey];
      } else if (col.flexGrow !== undefined) {
        flex = col.flexGrow;
      } else if (maxWidth > 200) {
        flex = 2;
      } else if (maxWidth > 150) {
        flex = 1.5;
      }

      widths[col.columnKey] = { minWidth: maxWidth, flex };
    });

    return widths;
  }, [data, columns, screenWidth, columnFlexValues]);

  useEffect(() => {
    setColumnWidths(calculateColumnWidths);
  }, [calculateColumnWidths]);

  const sortedData = useMemo(() => {
    if (isExternalPagination) return data;
    if (!sortConfig) return data;
    return [...data].sort((a, b) => {
      const column = columns.find((col) => col.columnKey === sortConfig.key);
      if (!column) return 0;
      const aValue = column.getSortValue ? column.getSortValue(a) : a[column.columnKey];
      const bValue = column.getSortValue ? column.getSortValue(b) : b[column.columnKey];
      if (aValue == null && bValue == null) return 0;
      if (aValue == null) return sortConfig.direction === "asc" ? 1 : -1;
      if (bValue == null) return sortConfig.direction === "asc" ? -1 : 1;
      if (typeof aValue === "number" && typeof bValue === "number") {
        return sortConfig.direction === "asc" ? aValue - bValue : bValue - aValue;
      }
      if (aValue instanceof Date && bValue instanceof Date) {
        return sortConfig.direction === "asc"
          ? aValue.getTime() - bValue.getTime()
          : bValue.getTime() - aValue.getTime();
      }
      const aStr = String(aValue).toLowerCase();
      const bStr = String(bValue).toLowerCase();
      if (aStr < bStr) return sortConfig.direction === "asc" ? -1 : 1;
      if (aStr > bStr) return sortConfig.direction === "asc" ? 1 : -1;
      return 0;
    });
  }, [data, sortConfig, columns, isExternalPagination]);

  const totalPages = isExternalPagination
    ? (externalTotalPages as number)
    : Math.ceil(sortedData.length / rowsPerPage);
  const activePage = isExternalPagination ? (externalPage as number) : currentPage;
  const paginatedData = isExternalPagination
    ? sortedData
    : sortedData.slice((currentPage - 1) * rowsPerPage, currentPage * rowsPerPage);

  const totalTableWidth = useMemo(() => {
    return columns.reduce((sum, col) => {
      const w = columnWidths[col.columnKey];
      return sum + (w ? w.minWidth : (col.width ?? 120));
    }, 0);
  }, [columns, columnWidths]);

  const displayPage = isExternalPagination ? activePage + 1 : activePage;
  const atFirst = isFirstPage !== undefined
    ? isFirstPage
    : (isExternalPagination ? activePage === 0 : activePage === 1);
  const atLast = isLastPage !== undefined
    ? isLastPage
    : (isExternalPagination ? activePage >= totalPages - 1 : activePage === totalPages);
  const firstPageIndex = isExternalPagination ? 0 : 1;
  const lastPageIndex = isExternalPagination ? totalPages - 1 : totalPages;

  const effectiveWidth = containerWidth > 0
    ? Math.max(totalTableWidth, containerWidth)
    : totalTableWidth;

  const handleSort = useCallback((columnKey: string) => {
    let newDirection: 'asc' | 'desc' = 'asc';
    if (sortConfig?.key === columnKey) {
      newDirection = sortConfig.direction === "asc" ? "desc" : "asc";
      setSortConfig({ key: columnKey, direction: newDirection });
    } else {
      setSortConfig({ key: columnKey, direction: "asc" });
    }
    onSort?.(columnKey, newDirection);
  }, [sortConfig, onSort]);

  const showOverlayWithCustomContent = (content: any) => {
    setOverlayContent(content);
    setOverlayVisible(true);
  };

  const closeOverlay = () => {
    setOverlayVisible(false);
    setOverlayContent(null);
  };

  const getStatusStyle = (status: string) => {
    const normalizedStatus = status
      ?.toString()
      .trim()
      .toLowerCase()
      .replace(/[_-]+/g, " ");

    switch (normalizedStatus) {
      case "in progress":
        return {
          container: {
            backgroundColor: "#E5A408", height: 24, paddingHorizontal: 8, paddingVertical: 2,
            borderRadius: 4, alignItems: "center" as const, justifyContent: "center" as const,
            overflow: "hidden" as const, flexDirection: "row" as const,
            alignSelf: "flex-start" as const, minWidth: 80, maxWidth: 200,
          },
          text: { color: "#000000", fontWeight: "600" as const, fontSize: 12, textAlign: "center" as const, lineHeight: 16 },
        };
      case "completed":
      case "complete":
        return {
          container: {
            backgroundColor: "#027C06", height: 24, paddingHorizontal: 8, paddingVertical: 2,
            borderRadius: 4, alignItems: "center" as const, justifyContent: "center" as const,
            overflow: "hidden" as const, flexDirection: "row" as const,
            alignSelf: "flex-start" as const, minWidth: 80, maxWidth: 200,
          },
          text: { color: "#000000", fontWeight: "600" as const, fontSize: 12, textAlign: "center" as const, lineHeight: 16 },
        };
      case "active":
        return {
          container: {
            backgroundColor: "#005CBC", height: 24, paddingHorizontal: 8, paddingVertical: 2,
            borderRadius: 4, alignItems: "center" as const, justifyContent: "center" as const,
            overflow: "hidden" as const, flexDirection: "row" as const,
            alignSelf: "flex-start" as const, minWidth: 80, maxWidth: 200,
          },
          text: { color: "#000000", fontWeight: "600" as const, fontSize: 12, textAlign: "center" as const, lineHeight: 16 },
        };
      default:
        return {
          container: {
            backgroundColor: "#E5A408", height: 24, paddingHorizontal: 8, paddingVertical: 2,
            borderRadius: 4, alignItems: "center" as const, justifyContent: "center" as const,
            overflow: "hidden" as const, flexDirection: "row" as const,
            alignSelf: "flex-start" as const, minWidth: 80, maxWidth: 200,
          },
          text: { color: "#000000", fontWeight: "600" as const, fontSize: 12, textAlign: "center" as const, lineHeight: 16 },
        };
    }
  };

  const renderRow = ({ item, index }: { item: any; index: number }) => {
    const isSelected = selectedRowIndex === index;

    const handleRowPress = () => {
      setSelectedRowIndex(index);
      if (onRowClick) {
        onRowClick(item);
      } else {
        showOverlayWithCustomContent(item);
      }
    };

    // Legacy per-cell click — kept for non-standard column types (pulse check, shift handover)
    const handleCellClick = (col: Column, cellValue: any) => {
      if (col.columnKey === "type") {
        if (onRowClick) onRowClick({ ...item, isPulseCheckNavigation: true });
        return;
      }
      if (col.columnKey === "Title" || col.columnKey === "Shift") {
        if (onRowClick) onRowClick({ ...item, isShiftHandOverNavigation: true, columnKey: col.columnKey });
        return;
      }
      handleRowPress();
    };

    const hasCustomCells = columns.some((c) => c.renderCell);

    const rowView = (
      <TouchableOpacity
        style={[styles.row, hasCustomCells && styles.rowAutoHeight, isSelected && styles.selectedRow]}
        testID="table-row"
        key={`row-${index}`}
        onPress={handleRowPress}
        activeOpacity={onRowClick ? 0.7 : 1}
      >
        {columns.map((col) => (
          <View
            key={col.columnKey}
            style={[
              styles.cellContainer,
              hasCustomCells && styles.cellContainerStretch,
              col.renderCell && styles.cellContainerAutoHeight,
              columnWidths[col.columnKey] && {
                minWidth: columnWidths[col.columnKey].minWidth,
                flex: columnWidths[col.columnKey].flex,
                ...(Platform.OS !== "web" && { width: columnWidths[col.columnKey].minWidth }),
              },
            ]}
          >
            {col.renderCell ? (
              <React.Fragment key={`custom-cell-${col.columnKey}-${index}`}>
                {col.renderCell(item)}
              </React.Fragment>
            ) : col.columnKey === "action" && item[col.columnKey] === "Resume" ? (
              <LhButton
                key={`action-button-${index}`}
                size="small"
                text={item[col.columnKey]}
                style={{ width: 100 }}
                onPress={() => console.log(`Action clicked for row ${index}`)}
              />
            ) : (
              <TouchableOpacity
                key={`cell-${col.columnKey}-${index}`}
                onPress={() => handleCellClick(col, item[col.columnKey])}
                style={[styles.cellTouchable, { alignItems: "flex-start", width: "100%" }]}
              >
                {(() => {
                  const colKeyLower = String(col.columnKey ?? "").toLowerCase();
                  const headerLower = String(col.header ?? "").toLowerCase();
                  const isStatusColumn =
                    colKeyLower === "status" || colKeyLower === "action" || headerLower === "status";
                  const cellValue = item[col.columnKey];

                  if (isStatusColumn) {
                    const statusStyle = getStatusStyle(cellValue);
                    return (
                      <View style={statusStyle.container}>
                        <Text style={statusStyle.text}>{cellValue}</Text>
                      </View>
                    );
                  }

                  return (
                    <Text
                      style={[
                        styles.cell,
                        colKeyLower === "id" || headerLower === "location id"
                          ? styles.firstCellText
                          : styles.otherCellText,
                        colKeyLower === "type" ? styles.typeCell : {},
                      ]}
                      numberOfLines={2}
                      ellipsizeMode="tail"
                    >
                      {col.formatter && cellValue !== undefined && cellValue !== null
                        ? col.formatter(cellValue)
                        : cellValue ?? "—"}
                    </Text>
                  );
                })()}
              </TouchableOpacity>
            )}
          </View>
        ))}
      </TouchableOpacity>
    );

    const expansionNode = expansionRenderer ? expansionRenderer(item) : null;
    return (
      <React.Fragment key={`row-frag-${index}`}>
        {rowView}
        {expansionNode}
      </React.Fragment>
    );
  };

  const rowsPerPageOptions = [10, 25, 50, 100];

  const goToPage = (p: number) => {
    if (isExternalPagination) {
      onExternalPageChange?.(p);
    } else {
      setCurrentPage(p);
    }
  };

  const handleRowsPerPageChange = (newRowsPerPage: number) => {
    setRowsPerPage(newRowsPerPage);
    setCurrentPage(1);
    onPageSizeChange?.(newRowsPerPage);
  };

  const renderMobilePagination = () => (
    <View style={styles.mobilePaginationContainer}>
      <View style={styles.mobilePaginationBox}>
        <TouchableOpacity
          disabled={atFirst}
          onPress={() => goToPage(activePage - 1)}
          style={[styles.mobilePaginationCell, styles.mobilePaginationArrowCell, atFirst && styles.mobilePaginationCellDisabled]}
          testID="mobile-prev-page-button"
          accessibilityLabel={locale?.prevPageLabel ?? 'Previous page'}
        >
          <MaterialIcons name="chevron-left" size={16} color={atFirst ? "#A0A0A0" : "#005CBC"} />
        </TouchableOpacity>
        <View style={[styles.mobilePaginationCell, styles.mobilePaginationCurrentCell]}>
          <Text style={styles.mobilePaginationCurrentText}>{displayPage}</Text>
        </View>
        <View style={[styles.mobilePaginationCell, styles.mobilePaginationNextCell]}>
          <Text style={styles.mobilePaginationNextText}>{locale?.pageOfLabel ?? 'of'} {totalPages}</Text>
        </View>
        <TouchableOpacity
          disabled={atLast}
          onPress={() => goToPage(activePage + 1)}
          style={[styles.mobilePaginationCell, styles.mobilePaginationArrowCell, atLast && styles.mobilePaginationCellDisabled]}
          testID="mobile-next-page-button"
          accessibilityLabel={locale?.nextPageLabel ?? 'Next page'}
        >
          <MaterialIcons name="chevron-right" size={16} color={atLast ? "#A0A0A0" : "#005CBC"} />
        </TouchableOpacity>
      </View>
    </View>
  );

  const renderDesktopPagination = () => (
    <View style={styles.paginationContainer}>
      <View style={styles.rowsPerPageContainer}>
        <Text style={styles.rowsPerPageLabel}>{locale?.rowsPerPageLabel ?? 'Rows per page'}</Text>
        <View style={{ position: "relative" }}>
          <TouchableOpacity
            style={styles.dropdownContainer}
            onPress={() => setDropdownVisible(!dropdownVisible)}
            testID="rows-per-page-dropdown"
            accessibilityLabel={locale?.rowsPerPageLabel ?? 'Rows per page selector'}
          >
            <Text style={styles.dropdownValue}>{rowsPerPage}</Text>
            <MaterialIcons name={dropdownVisible ? "keyboard-arrow-up" : "keyboard-arrow-down"} size={20} color="#616161" />
          </TouchableOpacity>
          {dropdownVisible && (
            <View style={styles.dropdownOptionsContainer}>
              {rowsPerPageOptions.map((option) => (
                <TouchableOpacity
                  key={option}
                  style={[styles.dropdownOption, option === rowsPerPage && styles.dropdownOptionSelected]}
                  onPress={() => { handleRowsPerPageChange(option); setDropdownVisible(false); }}
                  testID={`rows-per-page-option-${option}`}
                  accessibilityLabel={`${option} rows per page`}
                >
                  <Text style={[styles.dropdownOptionText, option === rowsPerPage && styles.dropdownOptionTextSelected]}>
                    {option}
                  </Text>
                </TouchableOpacity>
              ))}
            </View>
          )}
        </View>
      </View>

      <View style={styles.pageNavigationContainer}>
        <TouchableOpacity disabled={atFirst} onPress={() => goToPage(firstPageIndex)} style={styles.navigationButtonNoBorder} testID="first-page-button" accessibilityLabel={locale?.firstPageLabel ?? 'First page'}>
          <MaterialIcons name="first-page" size={20} color={atFirst ? "#D0D0D0" : "#034895"} />
        </TouchableOpacity>
        <TouchableOpacity disabled={atFirst} onPress={() => goToPage(activePage - 1)} style={styles.navigationButtonNoBorder} testID="prev-page-button" accessibilityLabel={locale?.prevPageLabel ?? 'Previous page'}>
          <MaterialIcons name="keyboard-arrow-left" size={20} color={atFirst ? "#D0D0D0" : "#034895"} />
        </TouchableOpacity>
        <View style={styles.pageIndicatorContainer}>
          <Text style={styles.pageText}>{locale?.pageLabel ?? 'Page'}</Text>
          <View style={styles.currentPageBox}>
            <Text style={styles.currentPageText}>{displayPage}</Text>
          </View>
          <Text style={styles.pageOfText}>{locale?.pageOfLabel ?? 'of'} <Text style={styles.totalPagesText}>{totalPages}</Text></Text>
        </View>
        <TouchableOpacity disabled={atLast} onPress={() => goToPage(activePage + 1)} style={styles.navigationButtonNoBorder} testID="next-page-button" accessibilityLabel={locale?.nextPageLabel ?? 'Next page'}>
          <MaterialIcons name="keyboard-arrow-right" size={20} color={atLast ? "#D0D0D0" : "#034895"} />
        </TouchableOpacity>
        <TouchableOpacity disabled={atLast} onPress={() => goToPage(lastPageIndex)} style={styles.navigationButtonNoBorder} testID="last-page-button" accessibilityLabel={locale?.lastPageLabel ?? 'Last page'}>
          <MaterialIcons name="last-page" size={20} color={atLast ? "#D0D0D0" : "#034895"} />
        </TouchableOpacity>
      </View>
    </View>
  );

  const renderPaginationControls = () => {
    const isIOSMobile = Platform.OS === "ios" && screenWidth < 768;
    if (isIOSMobile) return renderMobilePagination();
    return renderDesktopPagination();
  };

  return (
    <TouchableWithoutFeedback onPress={() => { setDropdownVisible(false); setFilterModal(null); }}>
      <View
        style={styles.container}
        onLayout={(e) => setContainerWidth(e.nativeEvent.layout.width)}
      >
        <ScrollView
          horizontal
          showsHorizontalScrollIndicator={true}
          style={{ flexGrow: 1 }}
          contentContainerStyle={{ minWidth: effectiveWidth }}
          scrollEventThrottle={16}
          nestedScrollEnabled={true}
        >
          <View style={{ width: effectiveWidth, minWidth: effectiveWidth }}>
            <HarborTableHeaderRow
              columns={columns}
              sortConfig={sortConfig}
              columnWidths={columnWidths}
              showHeaderTopBorder={showHeaderTopBorder}
              isIOSMobile={isIOSMobile}
              onSort={handleSort}
              columnFilters={columnFilters}
              dateRangeActive={dateRangeActive}
              activeFilterKey={filterModal?.columnKey ?? drFilterModal?.columnKey ?? null}
              onOpenFilter={handleOpenFilter}
              onOpenDateRangeFilter={handleOpenDateRangeFilter}
            />
            {isLoading ? (
              <HarborSkeletonRows columns={columns} columnWidths={columnWidths} rowCount={10} />
            ) : paginatedData.length > 0 ? (
              <View
                style={
                  Platform.OS === "ios" || Platform.OS === "android"
                    ? { flex: 1, maxHeight: 800, minHeight: 500 }
                    : { flex: 1, minHeight: 500 }
                }
              >
                <FlatList
                  data={paginatedData}
                  renderItem={renderRow}
                  keyExtractor={(_, index) => index.toString()}
                  scrollEnabled={false}
                  testID="route-table-list"
                />
              </View>
            ) : (
              <View style={styles.noDataContainer}>
                <Text style={styles.noDataText}>{locale?.noDataLabel ?? 'No data available'}</Text>
              </View>
            )}
          </View>
        </ScrollView>
        {!isLoading && sortedData.length > 0 && renderPaginationControls()}

        {/* Column filter dropdown */}
        {!!filterModal && (
          <HarborFilterdropdown
            hideTrigger
            isOpen
            onOpenChange={(open) => {
              if (!open) setFilterModal(null);
            }}
            forcedPosition={{ top: filterModal.top, left: filterModal.left, width: filterModal.width }}
            overlayBackgroundColor="rgba(0,0,0,0.01)"
            autoFocusSearchOnOpen
            sortItems
            childList={filterModal.options.map((opt) => ({ id: opt.value, displayText: opt.label }))}
            selectedValue={(() => {
              const selectedIds = columnFilters[filterModal.columnKey] ?? [];
              if (selectedIds.length === 0) return [];
              return filterModal.options
                .filter((opt) => selectedIds.includes(opt.value))
                .map((opt) => ({ id: opt.value, displayText: opt.label }));
            })()}
            setSelectedValue={(items) => {
              if (items.length === 0) {
                onColumnFilter?.(filterModal.columnKey, []);
                return;
              }
              const nextValues = items
                .map((item) => String(item.id));
              onColumnFilter?.(filterModal.columnKey, nextValues);
            }}
            placeholder="All"
          />
        )}

        {/* Date range filter dropdown */}
        <DateRangeFilterDropdown
          visible={!!drFilterModal}
          fromDate={drFilterModal?.dateRangeFilter.fromDate ?? null}
          toDate={drFilterModal?.dateRangeFilter.toDate ?? null}
          top={drFilterModal?.top ?? 0}
          left={drFilterModal?.left ?? 0}
          onFromChange={(d) => {
            drFilterModal?.dateRangeFilter.onFromChange(d);
            setDrFilterModal((prev) =>
              prev ? { ...prev, dateRangeFilter: { ...prev.dateRangeFilter, fromDate: d } } : prev
            );
          }}
          onToChange={(d) => {
            drFilterModal?.dateRangeFilter.onToChange(d);
            setDrFilterModal((prev) =>
              prev ? { ...prev, dateRangeFilter: { ...prev.dateRangeFilter, toDate: d } } : prev
            );
          }}
          onClear={() => drFilterModal?.dateRangeFilter.onClear()}
          onClose={() => setDrFilterModal(null)}
        />

        <LHOverlay
          visible={overlayVisible}
          onDismiss={closeOverlay}
          title={overlayContent ? `${overlayContent.type} Details` : "Row Details"}
          size="large"
        >
          {overlayContent && (
            <View style={styles.overlayContent}>
              {/* Overlay content rendering */}
            </View>
          )}
        </LHOverlay>
      </View>
    </TouchableWithoutFeedback>
  );
};

export default HarborDataTable;

const baseStyles = StyleSheet.create({
  container: {
    flex: 1,
  },
  toggleContainer: {
    alignItems: "center",
    justifyContent: "center",
    width: "100%",
    height: "100%",
    minHeight: 40,
  },
  cellContainer: {
    flexDirection: "row",
    alignItems: "center",
    justifyContent: "flex-start",
    paddingHorizontal: 16,
    paddingVertical: 8,
    flex: 0,
    overflow: "hidden",
    minHeight: 48,
    height: 48,
  },
  centerContent: {
    alignItems: "center",
    justifyContent: "center",
  },
  headerRow: {
    flexDirection: "row",
    alignItems: "center",
    backgroundColor: "transparent",
    height: 48,
    minHeight: 48,
    borderBottomWidth: 2,
    borderBottomColor: "#D7E5F8",
    width: "100%",
    zIndex: 1,
  },
  row: {
    flexDirection: "row",
    paddingVertical: 0,
    borderBottomWidth: 2,
    borderBottomColor: "#D7E5F8",
    width: "100%",
    flex: 1,
    minHeight: 48,
    height: 48,
    alignItems: "center",
  },
  rowAutoHeight: {
    height: 'auto' as any,
    alignItems: 'center',
  },
  cellContainerStretch: {
    height: 'auto' as any,
    alignItems: 'center',
    justifyContent: 'flex-start',
    paddingVertical: 10,
  },
  cellContainerAutoHeight: {
    flexDirection: 'column',
    alignItems: 'flex-start',
    justifyContent: 'center',
  },
  selectedRow: {
    backgroundColor: "#e0f7fa",
  },
  cellTouchable: {
    flex: 1,
    justifyContent: "center",
    alignItems: "flex-start",
    width: "100%",
    overflow: "hidden",
    paddingHorizontal: 4,
    paddingVertical: 0,
  },
  cell: {
    fontSize: Platform.OS === "web" ? 14 : 12,
    flexShrink: 1,
    width: "100%",
    flexWrap: "wrap",
    flexDirection: "row",
    textAlignVertical: "center",
    fontWeight: "400",
    fontFamily: FontFamily.regular,
  },
  headerCellText: {
    fontWeight: "700",
    fontSize: 12,
    color: "#616161",
    textTransform: "uppercase",
    lineHeight: 18,
    includeFontPadding: false,
    padding: 0,
    width: "auto",
    flexShrink: 0,
    textAlign: "left",
    fontFamily: FontFamily.bold,
  },
  firstCellText: {
    color: "#005CBC",
    fontWeight: "500",
    fontFamily: FontFamily.regular,
  },
  otherCellText: {
    color: "#3A3A3A",
    fontFamily: FontFamily.regular,
  },
  typeCell: {
    color: "#FF6720",
    fontWeight: "bold",
    fontFamily: FontFamily.bold,
  },
  overlayContent: {
    padding: Platform.OS === "web" ? 16 : 12,
  },
  noDataContainer: {
    flex: 1,
    justifyContent: "center",
    alignItems: "center",
    padding: 20,
    minHeight: 100,
  },
  noDataText: {
    fontSize: 14,
    color: "#616161",
    fontFamily: FontFamily.regular,
  },
  paginationContainer: {
    flexDirection: "row",
    alignItems: "center",
    justifyContent: "space-between",
    paddingHorizontal: 16,
    paddingVertical: 12,
    borderTopWidth: 1,
    borderTopColor: "#E0E0E0",
    gap: 24,
  },
  rowsPerPageContainer: {
    flexDirection: "row",
    alignItems: "center",
    gap: 8,
  },
  rowsPerPageLabel: {
    fontSize: 13,
    color: "#616161",
    fontFamily: FontFamily.regular,
  },
  dropdownContainer: {
    flexDirection: "row",
    alignItems: "center",
    borderWidth: 1,
    borderColor: "#D0D0D0",
    borderRadius: 4,
    paddingHorizontal: 8,
    paddingVertical: 4,
    gap: 4,
    minWidth: 56,
  },
  dropdownValue: {
    fontSize: 13,
    color: "#3A3A3A",
    fontFamily: FontFamily.regular,
  },
  dropdownOptionsContainer: {
    position: "absolute",
    bottom: "100%",
    left: 0,
    backgroundColor: "#FFFFFF",
    borderWidth: 1,
    borderColor: "#D0D0D0",
    borderRadius: 4,
    zIndex: 100,
    minWidth: 56,
    ...Platform.select({
      web: { boxShadow: "0 2px 8px rgba(0,0,0,0.15)" } as any,
      default: { elevation: 4 },
    }),
  },
  dropdownOption: {
    paddingHorizontal: 10,
    paddingVertical: 7,
  },
  dropdownOptionSelected: {
    backgroundColor: "#EBF3FF",
  },
  dropdownOptionText: {
    fontSize: 13,
    color: "#3A3A3A",
    fontFamily: FontFamily.regular,
  },
  dropdownOptionTextSelected: {
    color: "#005CBC",
    fontFamily: FontFamily.bold,
  },
  pageNavigationContainer: {
    flexDirection: "row",
    alignItems: "center",
    gap: 2,
  },
  navigationButtonNoBorder: {
    width: 32,
    height: 32,
    alignItems: "center",
    justifyContent: "center",
    borderRadius: 4,
  },
  pageIndicatorContainer: {
    flexDirection: "row",
    alignItems: "center",
    gap: 6,
    paddingHorizontal: 4,
  },
  pageText: {
    fontSize: 13,
    color: "#616161",
    fontFamily: FontFamily.regular,
  },
  currentPageBox: {
    borderWidth: 1,
    borderColor: "#D0D0D0",
    borderRadius: 4,
    paddingHorizontal: 8,
    paddingVertical: 3,
    minWidth: 32,
    alignItems: "center",
  },
  currentPageText: {
    fontSize: 13,
    color: "#3A3A3A",
    fontFamily: FontFamily.bold,
  },
  pageOfText: {
    fontSize: 13,
    color: "#616161",
    fontFamily: FontFamily.regular,
  },
  totalPagesText: {
    fontFamily: FontFamily.bold,
    color: "#3A3A3A",
  },
  mobilePaginationContainer: {
    alignItems: "center",
    paddingVertical: 10,
    borderTopWidth: 1,
    borderTopColor: "#E0E0E0",
  },
  mobilePaginationBox: {
    flexDirection: "row",
    borderWidth: 1,
    borderColor: "#D0D0D0",
    borderRadius: 6,
    overflow: "hidden",
  },
  mobilePaginationCell: {
    paddingHorizontal: 12,
    paddingVertical: 7,
    alignItems: "center",
    justifyContent: "center",
    borderRightWidth: 1,
    borderRightColor: "#D0D0D0",
  },
  mobilePaginationArrowCell: {
    paddingHorizontal: 10,
  },
  mobilePaginationCellDisabled: {
    backgroundColor: "#F5F5F5",
  },
  mobilePaginationCurrentCell: {
    backgroundColor: "#005CBC",
    paddingHorizontal: 14,
  },
  mobilePaginationCurrentText: {
    fontSize: 13,
    color: "#FFFFFF",
    fontFamily: FontFamily.bold,
  },
  mobilePaginationNextCell: {
    borderRightWidth: 0,
    paddingHorizontal: 14,
  },
  mobilePaginationNextText: {
    fontSize: 13,
    color: "#616161",
    fontFamily: FontFamily.regular,
  },
  // ── Filter dropdown ────────────────────────────────────────────────────────
  filterIconBtn: {
    paddingHorizontal: 3,
    paddingVertical: 2,
    marginLeft: 2,
    borderRadius: 3,
  },
  filterIconBtnActive: {
    backgroundColor: '#EBF3FF',
  },
  // ── Header icons – each icon is its own fixed-size touchable so they never
  //    overlap the label or each other ──────────────────────────────────────
  sortIconBtn: {
    width: 20,
    height: 20,
    alignItems: "center",
    justifyContent: "center",
    flexShrink: 0,
  },
});
